/**
 * Réémission d'une URL de galerie expirée.
 *
 * LE PROBLÈME
 * Une image ajoutée depuis la galerie garde dans son calque l'URL SIGNÉE que le
 * serveur avait produite, et cette signature ne vaut qu'une heure. Passé ce
 * délai — un onglet resté ouvert, ou un projet rouvert le lendemain, puisque les
 * calques sont persistés — le stockage répond 403 et l'image ne charge plus.
 * La gomme avalait ce refus (`.catch(() => null)`) : l'outil s'ouvrait sur une
 * surface vide, sans un mot.
 *
 * CE QUI REND LA RÉPARATION POSSIBLE
 * La clé de stockage est construite comme `…/gallery/originals/{id}.png`, où
 * `{id}` est l'identifiant de la ligne en base. On peut donc le relire dans
 * l'URL et redemander une signature fraîche — y compris pour les calques posés
 * AVANT ce correctif, qui n'ont rien eu à mémoriser.
 *
 * CE QUE ÇA NE RÉSOUT PAS
 * Le fond du problème reste qu'un calque mémorise une URL périssable. La vraie
 * réponse serait une adresse stable côté serveur ; c'est un chantier à part,
 * parce qu'une balise <img> ne peut pas porter d'en-tête d'authentification.
 */
import { getGalleryImage } from '../api/galleryClient';

const RE_ID_GALERIE = /\/gallery\/originals\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\./i;

/** Identifiant de galerie contenu dans une URL, ou `null`. */
export function galleryIdFromUrl(url) {
  if (typeof url !== 'string') return null;
  const m = url.match(RE_ID_GALERIE);
  return m ? m[1] : null;
}

/**
 * Redemande une URL signée fraîche pour cette image de galerie.
 * Renvoie `null` si l'URL n'en vient pas, ou si l'image n'existe plus.
 */
export async function refreshGalleryUrl(url) {
  const id = galleryIdFromUrl(url);
  if (!id) return null;
  try {
    const img = await getGalleryImage(id);
    return img?.image_url || null;
  } catch (e) {
    // Image supprimée, session expirée, réseau : rien à réémettre.
    console.warn('Réémission de l’URL de galerie impossible:', e);
    return null;
  }
}

/**
 * Charge une image en réessayant une fois avec une URL fraîche si le premier
 * essai échoue.
 *
 * `charger` est la fonction de chargement à employer (injectée pour éviter une
 * dépendance croisée entre modules d'utilitaires).
 * `onUrlRenouvelee` est appelée avec la nouvelle URL quand la réémission a
 * servi : l'appelant peut alors la mémoriser, sans quoi chaque remontage
 * refera le même aller-retour.
 *
 * Renvoie `{ img, url, erreur }`. `img` vaut `null` si les deux essais ont
 * échoué — et dans ce cas `erreur` est renseignée, pour que l'appelant puisse
 * le DIRE au lieu de l'avaler.
 */
export async function loadImageWithRefresh(url, charger, onUrlRenouvelee) {
  try {
    return { img: await charger(url), url, erreur: null };
  } catch (premiereErreur) {
    const fraiche = await refreshGalleryUrl(url);
    if (!fraiche || fraiche === url) {
      return { img: null, url, erreur: premiereErreur };
    }
    try {
      const img = await charger(fraiche);
      if (typeof onUrlRenouvelee === 'function') onUrlRenouvelee(fraiche);
      return { img, url: fraiche, erreur: null };
    } catch (secondeErreur) {
      return { img: null, url, erreur: secondeErreur };
    }
  }
}
